/**
 * ⭐ `AW19` · **El hilo, partido en sesiones de 24 h**, con el estado de la actual.
 *
 * ⛔⛔ **INFORMA. NO BLOQUEA.** Acá no hay —ni puede haber— nada que impida mandar.
 * `modules/conversation/src/sesion/ventana.ts` no rechaza al vencer: **abre una
 * sesión nueva**, y el `id_conversacion` no cambia. Ver `sesiones.ts` §①.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ EL HUECO, DECLARADO: UN ENTRANTE NO TIENE `id_sesion` Y NO SE LO INVENTA
 * ════════════════════════════════════════════════════════════════════════════
 *
 * **Sólo las SALIENTES traen `id_sesion`.** `EntregaEnLaRed` lo lleva; el
 * `POST /entrada` devuelve **`{ outcome }` y nada más** (`apps/bot-web/src/main.ts`),
 * así que **la pantalla nunca se entera de en qué sesión quedó escrito lo que
 * mandó.** El `id_sesion` lo asigna el módulo al ESCRIBIR y es inmutable, pero no
 * vuelve por este camino.
 *
 * ⇒ **el corte de sesión se dibuja donde cambia el `id_sesion` de una SALIENTE**, y
 * un entrante se dibuja donde ocurrió, **sin adjudicarle sesión**. La alternativa
 * —adivinarle una por el tiempo— sería una **segunda opinión** sobre algo que el
 * motor decidió, y discreparía justo en el borde de la ventana, que es el único
 * lugar donde alguien está mirando.
 *
 * ⚠️ **Consecuencia visible, y no se tapa:** una conversación de puros entrantes
 * —todos con desenlaces que no producen entrega— **no muestra ningún corte**, porque
 * no hay ninguna saliente que lo revele. **Es correcto**: nadie sabe dónde iría.
 * Dueño del hueco: `apps-integrator` — lo cerraría que `POST /entrada` devolviera el
 * `id_sesion` junto al `outcome`, que hoy no hace.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⭐⭐ `AW27` · LOS OCHO DESENLACES SE DISTINGUEN A SIMPLE VISTA
 * ════════════════════════════════════════════════════════════════════════════
 *
 * ⛔⛔ **EL DEFECTO NO ERA QUE LA PANTALLA NO MOSTRARA EL DESENLACE: LO MOSTRABA.**
 * El defecto era que **los ocho se veían IGUAL** — medido el 28/8/2026:
 * `grep -c 'data-desenlace' apps/management-ui/src/estilos.css` ⇒ **0**. ⇒ un
 * operador que recibía `emisor-desconocido` **leía un mensaje enviado**, y por eso
 * QA reportó «no llega a bot-web» en vez de «el emisor no está enrolado».
 *
 * ⭐ **Se agrupan por lo que SIGNIFICAN para quien mira, no por su nombre**, y la
 * familia **no reemplaza al desenlace crudo**: el nombre exacto que devolvió el motor
 * se sigue leyendo al lado, porque es el dato con el que alguien diagnostica.
 *
 * ⛔⛔ **NO SE CODIFICA POR TONO SOLAMENTE.** `DESIGN.md` §«El estado se dibuja con el
 * TRAZO, no con el tono» lo tiene como regla del producto: *«`indexado` en verde y
 * `error` en rojo, con la misma forma, son la MISMA marca para quien no separa esos
 * tonos»*. ⇒ cada familia lleva **tres portadores además del color**: la familia
 * **escrita en palabras**, un **símbolo** (lleno · medio · vacío) y un **patrón de
 * trazo** (entero · cortado · doble), exactamente como `.banco__estado`.
 *
 * ⛔ **Y NO SE USA EL PAR DE BRAZOS.** `#0063c7` / `#ab21bb` **son el brazo** y tienen
 * valencia empatada a propósito (`ADR-0230`, Δ = 0,0029). Usarlos para «bien / mal»
 * rompería la propiedad más importante del producto: que ningún brazo se vea mejor
 * que el otro.
 *
 * ⚠️ **NINGÚN DESENLACE SE DIBUJA COMO ERROR DEL OPERADOR NI COMO FALLA DEL
 * SISTEMA.** `emisor-desconocido` es un desenlace **legítimo** del contrato y el
 * simulador existe para producirlo a pedido. **Por eso no hay rojo en ninguna de las
 * dos familias de desenlace**, y no hay verde en ninguna: el rojo queda donde ya
 * estaba, en `sin-motor`, que **no es un desenlace** —es el pedido que no llegó— y es
 * la única señal de esta pantalla que tiene que poder gritar.
 */

import type { JSX } from "react"
import { CierreDeSesion } from "./cierre-de-sesion.tsx"
import { agruparEnSesiones, estadoDeLaVentana, type EstadoDeLaVentana } from "./sesiones.ts"
import type { TurnoEnLaRed } from "./borde-simulador.ts"

/** Una línea del hilo, con las dos clases que no comparten forma (ver `chat.tsx`). */
export type LineaVisible =
  | {
      readonly clase: "entrante"
      readonly id: number
      readonly resumen: string
      /**
       * ⭐⭐ `AW36` · **El tipo, que hasta hoy se perdía en el camino.** `chat.tsx` lo
       * recibía, lo mandaba al borde y no lo guardaba en la línea; acá ni siquiera
       * estaba declarado. Es el campo que la marca de tipo dibuja.
       */
      readonly tipo: string
      readonly desenlace: string | null
      readonly fallo: string | null
    }
  | {
      readonly clase: "saliente"
      readonly id: number
      readonly contenido: string
      readonly fecha_hora: string
      readonly id_sesion: string
      /**
       * ⭐⭐ RASTRO · `ADR-0292` / `B22` · 16/9/2026 — **EL REGISTRO INTERNO DEL
       * TURNO QUE PRODUJO ESTA ENTREGA.**
       *
       * QUÉ: la fila de `conversation.turn_log` —camino, milisegundos, fragmentos,
       * puntaje del juez— que el motor escribió al contestar.
       * POR QUÉ: `B22` de QA, textual: *«endpoints de sólo lectura, **y además el
       * Simulador los muestra** — un acceso que sólo consume el runner es un acceso
       * que nadie mira»*.
       *
       * ⛔⛔ **`undefined` = TODAVÍA NO LLEGÓ, y se dibuja así** —«registro del turno
       * pendiente…»— en vez de rellenarse. La fila se escribe asíncrona y llega 1-3 s
       * después de la entrega; inventarle un camino o un `0 ms` sería exactamente la
       * confirmación que `ADR-0040 §②` prohibe, sobre el dato con el que un QA mide
       * el tiempo de respuesta.
       *
       * ⚠️ **Quién lo empareja, y con qué límite, está en `chat.tsx`**: no hay llave
       * entre una entrega y un turno —`turn_log` no tiene columna de participante ni
       * de conversación—, así que la correlación es **por brazo y por tiempo**.
       */
      readonly registro?: TurnoEnLaRed
    }

/**
 * ⭐⭐ `AW28` · Una línea de la sesión MÁS RECIENTE, tal como vino de
 * `GET conversaciones`. Los nombres son los de `FilaDeMensaje`, **sin traducir**.
 */
export type LineaDeSesion = {
  readonly id_sesion: string
  /** El hilo largo. No vence. Se muestra para que «sesión» y «conversación» no se confundan. */
  readonly id_conversacion: string
  /** ISO-8601. El cable lo revive a `Date`; acá vuelve a texto porque `sesiones.ts` come ISO. */
  readonly fecha_hora: string
  /** ⭐ `AW29` · Del bot o del usuario. **Es lo que decide el anclaje.** */
  readonly es_sistema: boolean
  readonly contenido: string
  /**
   * ⭐⭐ `AW41` · **EL DESENLACE, PEGADO A LA LÍNEA QUE LO PRODUJO.**
   *
   * ⛔⛔ El defecto que cierra, en palabras del dueño: *«en los mensajes se está
   * duplicando el que envío, aparece mi mensaje y luego aparece otro diciendo
   * encolado… el estado es importante pero no es un mensaje adicional»*.
   *
   * `chat.tsx` mandaba el entrante y anotaba el desenlace en una burbuja LOCAL; el
   * `pulso` releía el historial, que **ya tenía esa misma fila** con su instante real.
   * ⇒ **dos burbujas para un solo mensaje.** Ahora el desenlace VIAJA con la fila del
   * historial y la burbuja local se descarta. `undefined` = esta línea no la mandó
   * esta pestaña, así que **nadie sabe** su desenlace y no se inventa ninguno.
   *
   * ⛔ **Y esto NO le da color a nada.** Reusa `MarcaDeFamilia` y `MarcaDeTipo`, que
   * ya existen y ya decidieron su codificación en `ADR-0233 §③`: versalita, filete y
   * posición, **cero tono nuevo**. Lo único que cambia es que el estado deja de
   * ocupar un renglón propio, que es exactamente lo que el dueño pidió.
   */
  readonly desenlace?: string | null
  readonly fallo?: string | null
  readonly tipo?: string
}

/**
 * ⭐⭐ `AW27` · **Las TRES familias**, por lo que significan para quien mira.
 *
 * ⛔ **`rechazado` no es una lista de siete literales, y eso es deliberado.** La
 * pantalla **no tiene** la lista de los ocho —importarla sería un import de VALOR
 * desde `contracts/`, que rompe el build por diseño— y no la debe tener: un noveno
 * desenlace tiene que **seguir viéndose**, con su nombre crudo, el día que aparezca.
 * La regla es universal: *«¿el motor lo encoló?»*, y todo lo demás cae por la otra
 * rama **sin haber sido nombrado**.
 */
type Familia = "encolado" | "rechazado" | "sin-motor"

/**
 * El único literal de desenlace que esta pantalla conoce, y el único que necesita.
 * `contracts/channel/index.ts:437` lo define: *«Texto de un participante conocido:
 * persistido y **encolado**»*. Es el único de los ocho que ejecuta el turno.
 */
export const DESENLACE_ENCOLADO = "encolado"

/**
 * ⛔ **El símbolo y el rótulo son los portadores que NO son color.** El glifo va
 * `aria-hidden` porque el rótulo de al lado ya dice lo mismo en palabras: un lector
 * de pantalla que anuncie «círculo medio» no informa a nadie.
 *
 * ⚠️ **El rótulo de `rechazado` dice «no lo encoló» y no «lo rechazó», a propósito.**
 * `interactivo-salida` **hizo algo** —la baja ocurrió y la fila se escribió
 * (`contracts/channel/index.ts`: *«un `interactivo-salida` en la bitácora dice esta
 * persona quedó fuera»*)— y `modalidad` **contestó**. Llamarlos «rechazados» sería
 * una segunda opinión sobre lo que el motor ya dijo, que es lo que esta pantalla no
 * hace. Lo que las siete comparten, y es afirmable, es que **el turno no se encoló**.
 */
const FAMILIAS: Readonly<Record<Familia, { readonly glifo: string; readonly rotulo: string }>> = {
  encolado: { glifo: "●", rotulo: "Quedó encolado: el motor lo tomó" },
  rechazado: { glifo: "◐", rotulo: "El motor no lo encoló, y dijo por qué" },
  "sin-motor": { glifo: "○", rotulo: "No se pudo hablar con el motor" },
}

function familiaDe(desenlace: string | null, fallo: string | null): Familia {
  // ⛔ El orden importa: sin desenlace y con fallo, el pedido NO LLEGÓ, y eso no es
  //    un desenlace del motor. `hilo.tsx` ya lo distinguía; acá se le pone familia.
  if (fallo !== null) return "sin-motor"
  return desenlace === DESENLACE_ENCOLADO ? "encolado" : "rechazado"
}

/** La marca de familia: símbolo + palabras. El color lo pone `estilos.css`. */
function MarcaDeFamilia({ familia }: { readonly familia: Familia }): JSX.Element {
  const { glifo, rotulo } = FAMILIAS[familia]
  return (
    <span className="simulador__familia" data-familia={familia}>
      <span className="simulador__familia-glifo" aria-hidden="true">
        {glifo}
      </span>
      {rotulo}
    </span>
  )
}

/**
 * ⭐⭐ `AW36` · **LA MARCA DE TIPO, SIN COLOR PROPIO — Y ESO ESTÁ DECIDIDO POR ESCRITO.**
 *
 * ⛔⛔ El pedido del dueño decía *«podemos hacerlo con un badge o algo sutil»*, y
 * `ADR-0233` ③ registra que la mitad «badge» **no se toma**: el rastro de esta misma
 * pantalla dice, textual, *«sin esquinas redondeadas ni burbujas de color… el acento
 * es del brazo y de la selección, no del chat»*, y `estilos.css` dice *«un sello, no
 * una pastilla»*. Una pastilla verde/azul por tipo reintroduce el tono como portador
 * de significado y compite con el par de brazos, que es la única señal cromática que
 * este producto se permite.
 *
 * ⇒ la marca es **versalita + filete + posición**: la misma voz de rótulo que
 * `.simulador__quien`, separada de él por un capilar y pegada a él. **Cero color
 * nuevo** — usa el token que la burbuja ya le da a «quién habló», así que no entra
 * ningún par sin medir.
 *
 * ⛔ **Y dice el literal, no una traducción.** «Plantilla» y «mensaje estándar» —los
 * otros dos tipos que el dueño nombró— **no existen en el motor** (`ADR-0233`, la
 * cuarta decisión, la que NO se toma): el vocabulario es `texto` / `interactivo` y
 * «cualquier otro ⇒ `modalidad`». Traducir a palabras de producto obligaría a una
 * tabla, y una tabla deja afuera al tipo que el escape mande mañana.
 *
 * ⚠️ El rótulo oculto existe porque, leído en voz alta, «PARTICIPANTE · TEXTO» no
 * dice qué es ese segundo dato. El `data-tipo` es para que un examen lo lea sin
 * depender del texto, igual que `data-desenlace`.
 */
function MarcaDeTipo({ tipo }: { readonly tipo: string }): JSX.Element {
  return (
    <span className="simulador__tipo" data-tipo={tipo}>
      <span className="sim__oculto">Tipo de entrante: </span>
      {tipo}
    </span>
  )
}

/**
 * ⭐⭐ `ADR-0292` / `B22` · **EL RENGLÓN DE METADATOS DEL TURNO, DEBAJO DE CADA
 * RESPUESTA DEL MOTOR.**
 *
 * QUÉ: `respondido · 4,4 s · 2 fragmentos · juez 0,98`, y cuando aplica
 * `· regeneró 1`, `· contención: <categoria_disparada>`, `· moderación: <resultado>`
 * y `· fallback`.
 * POR QUÉ: `B22` pide que el endpoint que `ADR-0292` abrió **se vea en el Simulador**.
 *
 * ⛔ **El `camino` va CRUDO y en `<code>`**, por la misma razón que el desenlace del
 * entrante (① del encabezado de `chat.tsx`): es el literal que el operador lleva a un
 * ticket o a un `grep`. Traducir `sin-cobertura` a lenguaje de producto crearía un
 * segundo vocabulario y deja al octavo camino sin fila en la tabla.
 *
 * ⛔ **Rótulo chico y gris, sin color propio y sin pastilla.** `DESIGN.md` §Radios y
 * el rastro de esta pantalla —*«sin esquinas redondeadas ni burbujas de color… el
 * acento es del brazo y de la selección, no del chat»*— lo fijan: esto usa los tokens
 * que la burbuja ya tiene (`--tinta-tenue`, `--texto-micro`), **cero par nuevo**.
 *
 * ⚠️ **Lo que el cable trae y este renglón NO dibuja, dicho y no tapado:**
 * `reintentos_del_modelo` y `veredicto_del_juez`. La forma de la línea la fijó el
 * pedido y estos dos no entraron; están en `TurnoEnLaRed` y llegan al navegador, así
 * que agregarlos es una decisión de producto y no un cable nuevo. Dueño: `web-dev`.
 */
const LOCALE_DEL_REGISTRO = "es-CL"

/**
 * Los segundos, **con coma y un decimal**. `es-CL` EXPLÍCITO y no el del navegador:
 * es la misma decisión que `conversaciones/formato.ts` ya tomó —*«tomarlo del
 * navegador haría que la misma fecha se viera distinta según la máquina»*—.
 */
const FORMATO_SEGUNDOS = new Intl.NumberFormat(LOCALE_DEL_REGISTRO, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

/** El puntaje del juez. Hasta dos decimales: `1` se lee `1` y `0.98` se lee `0,98`. */
const FORMATO_JUEZ = new Intl.NumberFormat(LOCALE_DEL_REGISTRO, { maximumFractionDigits: 2 })

/**
 * Las piezas del renglón **después del camino**, en el orden en que se leen.
 *
 * ⛔ **Las condicionales se OMITEN, no se dibujan en cero.** `· regeneró 0` y
 * `· moderación: null` son ruido que empuja fuera de la vista lo que sí pasó; y un
 * `judge_score` en `null` **no es un cero**: quiere decir que no hubo juez.
 */
function piezasDelRegistro(r: TurnoEnLaRed): readonly string[] {
  const piezas: string[] = [
    `${FORMATO_SEGUNDOS.format(r.ms_transcurridos / 1000)} s`,
    `${String(r.fragmentos)} ${r.fragmentos === 1 ? "fragmento" : "fragmentos"}`,
  ]
  if (r.judge_score !== null) piezas.push(`juez ${FORMATO_JUEZ.format(r.judge_score)}`)
  if (r.regeneraciones > 0) piezas.push(`regeneró ${String(r.regeneraciones)}`)
  if (r.categoria_disparada !== null) piezas.push(`contención: ${r.categoria_disparada}`)
  if (r.resultado_de_moderacion !== null) piezas.push(`moderación: ${r.resultado_de_moderacion}`)
  if (r.fallback) piezas.push("fallback")
  return piezas
}

/**
 * ⛔⛔ **LOS TRES ESTADOS DEL REGISTRO SE DIBUJAN, Y NINGUNO ES EL SILENCIO.**
 *
 *   · llegó ⇒ el renglón con los números;
 *   · todavía no llegó ⇒ «registro del turno pendiente…» —la fila es asíncrona—;
 *   · `/turnos` falló ⇒ «registro no disponible (‹motivo›)».
 *
 * ⛔ **La tercera no rompe el chat y eso es la mitad del requisito**: el envío y la
 * lectura de entregas siguen su camino (ver `chat.tsx`), y lo único que se pierde es
 * este renglón —que lo dice—. Un simulador que se cae porque un endpoint de
 * diagnóstico contestó `409` cambia un dato de más por la pantalla entera.
 */
function RegistroDelTurno({
  registro,
  error,
}: {
  readonly registro: TurnoEnLaRed | undefined
  readonly error: string | null
}): JSX.Element {
  /*
   * ⛔⛔ **EL TURNO QUE YA LLEGÓ GANA SOBRE UN ERROR POSTERIOR, y el orden de estos
   * dos `if` ES la decisión.** `errorDeRegistro` es el estado de la ÚLTIMA lectura de
   * `/turnos`, que es uno solo para toda la pantalla; si a los diez minutos el
   * endpoint empieza a contestar `409`, poner el error primero **borraría los
   * milisegundos de todas las respuestas que ya se habían medido bien**. El dato no se
   * volvió falso porque la próxima lectura fallara. Lo que sí cambia es el bloque del
   * último turno del brazo, que es de la lectura y no de la burbuja: ahí el error manda.
   */
  if (registro !== undefined) {
    return (
      <span
        className="simulador__registro"
        aria-label="registro del turno"
        data-camino={registro.camino}
      >
        <code>{registro.camino}</code>
        {` · ${piezasDelRegistro(registro).join(" · ")}`}
      </span>
    )
  }
  if (error !== null) {
    return (
      <span
        className="simulador__registro simulador__registro--sin"
        aria-label="registro del turno"
      >
        registro no disponible ({error})
      </span>
    )
  }
  return (
    <span
      className="simulador__registro simulador__registro--sin"
      aria-label="registro del turno"
    >
      registro del turno pendiente…
    </span>
  )
}

function rotuloDeDuracion(ms: number): string {
  const horas = Math.floor(ms / 3_600_000)
  const minutos = Math.floor((ms % 3_600_000) / 60_000)
  if (horas > 0) return `${horas} h ${minutos} min`
  return `${minutos} min`
}

function rotuloDelEstado(e: EstadoDeLaVentana): string {
  switch (e.caso) {
    case "sin-mensajes":
      return "Todavía no hay ninguna línea escrita, así que no hay ventana que medir."
    /*
     * ⭐ `AW29` · Hay sesión y no hay anclaje. ⛔ **No se sustituye por la última
     * línea del bot**: ésa es exactamente la derivación que esta etapa corrige, y
     * decir «le quedan 23 h» con el número equivocado es peor que no decir nada.
     */
    case "sin-anclaje":
      return "La sesión no tiene todavía ningún mensaje del participante, y el bot no mueve el vencimiento: no hay desde cuándo medir."
    case "abierta":
      return `Sesión abierta · le quedan ${rotuloDeDuracion(e.restanteMs)}`
    case "vencida":
      return `Sesión vencida hace ${rotuloDeDuracion(e.desdeMs)} · tu próximo mensaje abre una sesión nueva`
  }
}

/**
 * El instante, como lo escribe el resto de la pantalla: crudo, sin reformatear.
 *
 * ⭐⭐ `AW37` · **`lineas` llega EN ORDEN CRONOLÓGICO —de la más vieja a la más
 * nueva— y acá se dibuja tal cual.** No se ordena de este lado a propósito: el
 * único que puede reponer el orden real es quien tiene la página cruda del
 * contrato, porque el desempate es por `seq` y `LineaDeSesion` no lo lleva. Lo hace
 * `chat.tsx`, con el `.reverse()` que está documentado ahí.
 */
/**
 * ⭐⭐ `AW42` · **UNA LÍNEA DEL HISTORIAL, DIBUJADA DENTRO DEL HILO ÚNICO.**
 *
 * ⛔⛔ Antes vivía adentro de `<section class="sim__antecedentes">`, que **tiene borde
 * y fondo propios**. Con `AW39` esa sección pasó a contener TODO el hilo paginado y
 * las líneas del sondeo quedaron afuera ⇒ **el cuadro agrupaba unas y dejaba otras
 * afuera**, y la que quedaba afuera era la respuesta más nueva. Es el defecto que el
 * dueño reportó: *«la respuesta queda fuera del cuadro que cubre a los mensajes»*.
 */
function LineaDelHistorial({ l }: { readonly l: LineaDeSesion }): JSX.Element {
  return (
    <div className={`sim__burbuja sim__burbuja--${l.es_sistema ? "saliente" : "entrante"}`}>
      {/*
        ⛔ `telefono` NO se dibuja aunque la fila lo traiga: es el teléfono en claro
        de un menor, y la política de `AW16` §④ sigue entera.
      */}
      {/*
        ⛔⛔ `AW57` · **ACÁ NO HAY MEDICIÓN, Y ESA AUSENCIA NO SE RELLENA.**
        El hilo se dibuja desde DOS cables: las líneas vivas salen de
        `GET /entregas` (`LineaVisible`) y éstas salen de `GET conversaciones`
        (`LineaDeSesion`). **El campo de `AP46` vive en `Entrega`, que es sólo el
        primer cable** ⇒ ninguna línea del historial lo tiene.

        ⛔ **Y NO se calcula restando dos `fecha_hora` de esta lista.** Es la
        variante que aparece justo cuando alguien ve que las líneas viejas quedaron
        sin número, y suena inocente porque las dos marcas están ahí. Produce un
        número **para el que nadie definió el apareamiento**: `FilaDeMensaje` no
        trae identificador de mensaje —ver el hueco declarado más abajo— y una
        entrega y una línea del historial **pueden ser la MISMA fila**. El
        resultado sería indistinguible del número real dibujado al lado.

        ⇒ mientras `GET conversaciones` no lleve el campo, la mitad vieja del hilo
        queda sin número, **y eso es correcto**. Cerrarlo es tocar
        `contracts/conversation`, que no es territorio de esta etapa.
      */}
      {l.tipo !== undefined ? (
        <span className="sim__rotulos">
          <span className="simulador__quien">Participante</span>
          <MarcaDeTipo tipo={l.tipo} />
        </span>
      ) : (
        <span className="simulador__quien">{l.es_sistema ? "Sistema" : "Participante"}</span>
      )}
      <span className="simulador__contenido">{l.contenido}</span>
      {(l.desenlace !== undefined || l.fallo != null) && (
        <MarcaDeFamilia familia={familiaDe(l.desenlace ?? null, l.fallo ?? null)} />
      )}
      {l.desenlace != null && (
        <code
          className="simulador__desenlace"
          data-desenlace={l.desenlace}
          data-familia={familiaDe(l.desenlace, l.fallo ?? null)}
        >
          {l.desenlace}
        </code>
      )}
      {l.fallo != null && (
        <span className="simulador__fallo" role="alert" data-familia="sin-motor">
          El pedido no llegó, así que no hay desenlace: {l.fallo}
        </span>
      )}
      <time className="simulador__instante" dateTime={l.fecha_hora}>
        {l.fecha_hora}
      </time>
    </div>
  )
}

function Antecedentes({
  lineas,
  onCerrarSesion,
}: {
  readonly lineas: readonly LineaDeSesion[]
  /**
   * ⭐⭐ `AW30` · Cierra la sesión vigente del hilo y relee. Ver
   * `cierre-de-sesion.tsx`.
   *
   * ⛔ **El botón vive acá y no en otro lado**: es la única parte de la pantalla
   * que tiene el `id_conversacion` —el hilo largo es lo que el puerto recibe— y es
   * donde el operador está leyendo cuál es la sesión que va a cerrar. Un botón
   * lejos del dato que nombra es un botón que se aprieta sin saber sobre qué.
   */
  readonly onCerrarSesion: (id_conversacion: string) => Promise<string | null>
}): JSX.Element | null {
  const primera = lineas[0]
  if (primera === undefined) return null

  return (
    <section className="sim__antecedentes" aria-labelledby="sim-antecedentes-titulo">
      <h3 id="sim-antecedentes-titulo" className="sim__rotulo">
        Sesión más reciente
      </h3>
      {/*
        ⛔⛔ «LA MÁS RECIENTE», NO «LA ÚNICA», Y ES UNA DISTINCIÓN MEDIDA. El 28/8/2026:
        la invariante «una sola sesión activa por participante» NO está impuesta —
        `sessions` no tiene columna de estado ni índice único, y `resolverSesion`
        (`ventana.ts:149-176`) hace `SELECT`+`INSERT` sin bloqueo—. La pantalla dice
        lo que puede afirmar.
      */}
      <p className="sim__ayuda">
        Es <strong>la más reciente</strong>, no «la única»: nada en la base impone que haya
        una sola sesión activa por participante. Sale de <code>admin-web</code> con tu sesión
        de operador, y se lee de a una página: de una sesión más larga se ve el tramo más
        nuevo.
      </p>
      <p className="sim__ayuda">
        <span className="sim__antecedentes-id">
          sesión <code>{primera.id_sesion}</code> · conversación{" "}
          <code>{primera.id_conversacion}</code>
        </span>
      </p>

      {/*
        ⭐⭐ `AW30` · EL BOTÓN, PEGADO A LA SESIÓN QUE VA A CERRAR. Va DESPUÉS del
        identificador y no antes: primero se lee sobre qué se está por actuar.
      */}
      <CierreDeSesion id_conversacion={primera.id_conversacion} onCerrar={onCerrarSesion} />

    </section>
  )
}

export function Hilo({
  lineas,
  antecedentes,
  ahora,
  onCerrarSesion,
  errorDeRegistro,
}: {
  readonly lineas: readonly LineaVisible[]
  /**
   * ⭐⭐ `AW28` · Las líneas de la sesión MÁS RECIENTE, de `GET conversaciones`,
   * **en orden cronológico** (`AW37`: el cable las trae al revés y `chat.tsx` las da
   * vuelta antes de pasarlas).
   *
   * ⛔ **Y son lo único que puede dar el anclaje de `AW29`.** `GET /entregas` viene
   * pre-filtrado a `es_sistema === true` y `Entrega` no lleva `es_sistema`, así que
   * por ese camino el anclaje correcto es **inalcanzable por construcción**.
   */
  readonly antecedentes: readonly LineaDeSesion[]
  /**
   * ⛔ El reloj entra por prop. Ver `sesiones.ts` §②: con `Date.now()` adentro, el
   * borde exacto de la ventana —el único punto donde importa— no se puede examinar.
   */
  readonly ahora: Date
  /**
   * ⭐⭐ `AW30` · Cierra la sesión vigente del hilo largo y **relee**. Baja hasta
   * `Antecedentes`, que es donde vive el `id_conversacion`.
   *
   * ⛔ Entra por prop y no se arma acá, por lo mismo que el reloj: la relectura del
   * historial la tiene `chat.tsx`, y un examen tiene que poder reemplazar el cierre
   * por un doble sin montar la pantalla entera.
   */
  readonly onCerrarSesion: (id_conversacion: string) => Promise<string | null>
  /**
   * ⭐⭐ `ADR-0292` / `B22` · El motivo CORTO de que el registro de turnos no esté,
   * o `null` si la última lectura anduvo.
   *
   * ⛔ **Entra por prop y no se lee acá**, igual que el reloj y el cierre de sesión:
   * quien habla con `/turnos` es `chat.tsx`, y un examen tiene que poder poner este
   * estado sin montar la pantalla entera.
   */
  readonly errorDeRegistro: string | null
}): JSX.Element {
  const salientes = lineas.filter(
    (l): l is Extract<LineaVisible, { clase: "saliente" }> => l.clase === "saliente",
  )

  /*
   * ⭐⭐ `AW29` · LA VENTANA SE MIDE SOBRE LAS DOS FUENTES JUNTAS, y el anclaje sale
   * de la única que puede darlo.
   *
   * Las entregas se suman con `es_sistema: true` **porque lo son**: `GET /entregas`
   * viene pre-filtrado a `es_sistema === true` (`apps/bot-web/src/entregas.ts:242`,
   * `filter(f => f.es_sistema)`). ⇒ **no mueven el anclaje** —`ventana.ts:122` es
   * `ruedaElAnclaje: !esSistema`— y lo único que aportan es qué sesión es la más
   * reciente cuando el motor abrió una después de la última lectura.
   *
   * ⚠️ **Una entrega y una línea del historial pueden ser la MISMA fila y contarse
   * dos veces.** No hay un identificador de mensaje en ninguno de los dos cables para
   * deduplicar. No afecta el anclaje —las dos son del sistema— ni el orden de las
   * sesiones. Hueco declarado.
   *
   * ⭐⭐⭐ RASTRO · `AW55` · 16/9/2026 — **EL PÁRRAFO DE ARRIBA ENUMERÓ DOS EJES DE
   * TRES, Y EL QUE FALTABA ERA EL ÚNICO QUE VE EL OPERADOR.** No se borra
   * (`ADR-0082`: redactar no es borrar) porque **sus dos veredictos siguen siendo
   * ciertos**: la fila doble no mueve el anclaje ni el orden de las sesiones. Lo que
   * nunca nombró es el **tercer eje: que la burbuja se dibuja dos veces** — y eso es
   * `H-AUD-018`, reportado por el dueño usando el producto: *«la burbuja repetida
   * conserva la marca de tiempo original»*, con el bloque duplicado creciendo un
   * renglón por turno.
   *
   * ⛔ **La lección, que vale más que el arreglo:** un hueco declarado NO es un hueco
   * medido. Esta prosa enumeró las consecuencias que se le ocurrieron al que la
   * escribió, sonó completa, y el día que faltó una **nada avisó** — porque una
   * prosa no tiene quien la haga cumplir. Lo que avisa hoy es
   * `pruebas/aw55-dos-fuentes.test.tsx`, que **puebla las dos fuentes a la vez**:
   * las 356 pruebas que pasaban no podían ver el defecto porque ningún archivo las
   * poblaba juntas.
   *
   * ⇒ **DÓNDE SE CERRÓ, Y POR QUÉ NO ACÁ.** La deduplicación vive en `chat.tsx`,
   * **antes** de pasar las listas —junto a la de los entrantes de `AW41`, con su
   * costo escrito encima—. Acá sería tarde: esta misma llamada arma `sesiones` con
   * `[...antecedentes, ...salientes]` y `estadoDeLaVentana` come esa lista, así que
   * filtrar al dibujar dejaría el conteo de sesiones alimentado con las filas dobles
   * — o sea, rompería precisamente el eje que el párrafo de arriba declaró «no
   * afectado». Las listas que llegan por prop **ya vienen deduplicadas**.
   *
   * ⚠️ **Lo que sigue abierto, dicho:** la llave de `chat.tsx` es DERIVADA
   * (`contenido` + instante + `id_sesion`), no una identidad. Cerrarlo de verdad
   * exige que `POST /entrada` devuelva el identificador de la fila que escribió, y
   * **eso es contrato**.
   */
  const sesiones = agruparEnSesiones([
    ...antecedentes,
    ...salientes.map((s) => ({
      id_sesion: s.id_sesion,
      fecha_hora: s.fecha_hora,
      es_sistema: true,
    })),
  ])
  const estado = estadoDeLaVentana(sesiones, ahora)

  /*
   * El corte se dibuja donde cambia el `id_sesion`. Ver el hueco declarado arriba: un
   * entrante del hilo VIVO no lleva sesión y no se le inventa una.
   *
   * ⭐ `AW42` · Arranca en el historial y **sigue** en el hilo vivo, porque ahora son
   * una sola lista: si la última del historial y la primera viva son de la misma
   * sesión, no se dibuja un corte que no ocurrió.
   */
  let sesionPrevia: string | null = null

  return (
    <>
      <p className="sim__ventana" data-estado={estado.caso}>
        <strong>{rotuloDelEstado(estado)}</strong>
        {estado.caso !== "sin-mensajes" && (
          <>
            {" "}
            <span className="sim__ventana-fuente">
              Lo calcula esta pantalla contra el reloj de tu navegador, no el motor: no hay ninguna
              consulta que conteste si una sesión está vencida.
            </span>
          </>
        )}
      </p>

      {/* ⭐ `AW28` · Lo que ya existía antes de esta pestaña, arriba de lo de ahora. */}
      <Antecedentes lineas={antecedentes} onCerrarSesion={onCerrarSesion} />

      {/*
        ⭐⭐ `AW42` · **UN SOLO HILO. NO DOS LISTAS.**
        ⛔⛔ Hasta acá había DOS `<ol class="simulador__hilo">`: una ADENTRO de
        `.sim__antecedentes` —que tiene borde y fondo— y otra afuera, para lo que
        traía el sondeo. Medido en Chrome: `lista_1_DENTRO_de_la_caja: true` con 10
        burbujas, `lista_2_DENTRO_de_la_caja: false`. ⇒ **el cuadro agrupaba unas
        líneas y dejaba otras afuera**, y la de afuera era la más nueva.
        ⇒ el historial y lo vivo son **la misma conversación** y van en la misma
        lista. El cuadro queda arriba, siendo lo que debe ser: la ficha de la sesión.
      */}
      <ol className="simulador__hilo">
        {antecedentes.map((l, i) => {
          const abre = l.id_sesion !== sesionPrevia
          const esLaPrimera = sesionPrevia === null
          sesionPrevia = l.id_sesion
          return (
            <li
              key={`hist-${l.fecha_hora}-${String(i)}`}
              className={`simulador__linea simulador__linea--${l.es_sistema ? "saliente" : "entrante"}`}
            >
              {/*
                ⭐⭐ `AW42` · **EL CORTE DE SESIÓN, AHORA TAMBIÉN EN EL HISTORIAL.**
                Antes sólo se dibujaba entre salientes del hilo vivo. Con `AW39` el
                operador **pagina hacia atrás y cruza sesiones**, así que sin esto
                estaría leyendo dos ventanas de 24 h distintas como si fueran una.
                Acá SÍ se puede afirmar: `FilaDeMensaje` trae `id_sesion` en cada
                fila, entrantes incluidos — que es lo que el cable de entregas no da.
              */}
              {abre && !esLaPrimera && (
                <p className="sim__corte" role="separator">
                  <span>Sesión nueva · el hilo largo no cambió</span>
                </p>
              )}
              <LineaDelHistorial l={l} />
            </li>
          )
        })}
        {lineas.map((linea) => {
          const abreSesion = linea.clase === "saliente" && linea.id_sesion !== sesionPrevia
          const primera = sesionPrevia === null
          if (linea.clase === "saliente") sesionPrevia = linea.id_sesion

          return (
            <li
              key={`${linea.clase}-${linea.id}`}
              className={`simulador__linea simulador__linea--${linea.clase}`}
            >
              {abreSesion && !primera && (
                <p className="sim__corte" role="separator">
                  <span>Sesión nueva · el hilo largo no cambió</span>
                </p>
              )}

              {linea.clase === "entrante" ? (
                <div className="sim__burbuja sim__burbuja--entrante">
                  {/* ⭐ `AW36` · «Entrante» → «Participante»: quién habló, no de qué
                      lado del cable vino. La marca de tipo va PEGADA al rótulo —canal
                      de rótulo— y no en el del contenido, que es de donde se sacó
                      «Texto libre». */}
                  <span className="sim__rotulos">
                    <span className="simulador__quien">Participante</span>
                    <MarcaDeTipo tipo={linea.tipo} />
                  </span>
                  <span className="simulador__contenido">{linea.resumen}</span>
                  {/*
                    ⭐⭐ `AW27` · LA FAMILIA, EN PALABRAS Y CON SÍMBOLO. Va ANTES del
                    literal crudo y **no lo reemplaza**: la familia dice qué significa
                    para quien mira, el literal es el dato con el que se diagnostica.
                  */}
                  <MarcaDeFamilia familia={familiaDe(linea.desenlace, linea.fallo)} />
                  {/*
                    ⭐⭐ EL DESENLACE CRUDO, AL LADO DEL MENSAJE. Tal cual vino, sin
                    traducir y sin agrupar. `data-desenlace` es para que un examen lo
                    lea sin depender del texto que lo rodea; `data-familia` es lo que
                    `estilos.css` alcanza para darle trazo y tono.
                  */}
                  {linea.desenlace !== null && (
                    <code
                      className="simulador__desenlace"
                      data-desenlace={linea.desenlace}
                      data-familia={familiaDe(linea.desenlace, linea.fallo)}
                    >
                      {linea.desenlace}
                    </code>
                  )}
                  {linea.fallo !== null && (
                    <span className="simulador__fallo" role="alert" data-familia="sin-motor">
                      El pedido no llegó, así que no hay desenlace: {linea.fallo}
                    </span>
                  )}
                </div>
              ) : (
                <div className="sim__burbuja sim__burbuja--saliente">
                  {/*
                    ⭐ `AW36` · «Respuesta» → «Sistema».
                    ⛔⛔ **Y del lado Sistema NO HAY MARCA DE TIPO, a propósito.**
                    `[medido 28/8/2026]` `Entrega` (`apps/bot-web/src/entregas.ts`)
                    tiene siete campos y **ninguno es el tipo**: la saliente no lo
                    lleva por el cable. Ponerle una —«Plantilla», o una deducida del
                    contenido— sería una afirmación de la pantalla sobre algo que el
                    motor nunca dijo, la misma falta que `AW16` documentó al negarse
                    a derivar el desenlace. Hueco declarado: lo cerraría que
                    `GET /entregas` devolviera el tipo, que hoy no hace.

                    ⭐⭐ RASTRO · `AW57` · 16/9/2026 — **«SIETE CAMPOS» QUEDÓ VIEJO:
                    HOY SON OCHO.** `[medido 16/9/2026]` `AP46` sumó
                    `ms_desde_el_entrante` a `Entrega`. No se borra el párrafo de
                    arriba (`ADR-0082`: redactar no es borrar) porque **su argumento
                    sigue entero y es el que importa**: el octavo campo tampoco es el
                    tipo, así que la saliente sigue sin llevarlo y la pantalla sigue
                    sin poder afirmarlo. Lo único que cambia es el número.

                    ⭐⭐ RASTRO DE LA VUELTA · 16/9/2026, EL MISMO DÍA — **VOLVIÓ A
                    SER SIETE.** `AP46` y `AW57` se retiraron del árbol: el dueño se
                    quedó con la implementación de `origin/master`, que mide OTRO
                    intervalo desde otra fuente (`ms_transcurridos`, por
                    `GET /turnos`). `ADR-0082`: tampoco se borra el RASTRO de arriba —
                    lo que dejó de ser cierto es su *««SIETE CAMPOS» QUEDÓ VIEJO: HOY
                    SON OCHO»*. El párrafo original del `AW36` vuelve a ser literal, y
                    el número hizo el recorrido **7 → 8 → 7**. El argumento nunca se
                    movió: ninguno de esos campos es el tipo, ni el octavo cuando
                    entró ni ahora que se fue.

                    ⚠️ **Y esta enmienda no la pedía ningún instrumento.** Ningún
                    `grep` del campo nuevo encuentra este renglón —no lo nombra— y
                    `tsc` no lee prosa: si no se tocaba acá, no lo tocaba nadie.
                  */}
                  <span className="simulador__quien">Sistema</span>
                  <span className="simulador__contenido">{linea.contenido}</span>
                  {/* ⛔ `destinatario` NO se dibuja: es el teléfono en claro. */}
                  {/*
                    ⭐⭐ `ADR-0292` / `B22` · **EL REGISTRO DEL TURNO, DEBAJO DE LA
                    BURBUJA DEL MOTOR.** Va DESPUÉS del contenido y ANTES del instante:
                    es metadato de cómo se produjo esta respuesta, no contenido de la
                    respuesta —la misma separación de canales que `AW36` hizo con la
                    marca de tipo—.
                  */}
                  <RegistroDelTurno registro={linea.registro} error={errorDeRegistro} />
                  <time className="simulador__instante" dateTime={linea.fecha_hora}>
                    {linea.fecha_hora}
                  </time>
                </div>
              )}
            </li>
          )
        })}
      </ol>
    </>
  )
}
